import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Heart, Users, Trophy, Target, ExternalLink, ChevronRight, BookOpen, Car, Share2, Sparkles, Zap, CircleDot, Eye, MousePointer, Instagram, Crosshair, CheckCircle } from "lucide-react";
import { Helmet } from "react-helmet-async";
import { toast } from "sonner";
import { useLanguage } from "../context/LanguageContext";
import { usePageContent } from "../hooks/usePageContent";

const GOFUNDME_DEFAULT = "https://gofund.me/274183352";
const VIDEO_DEFAULT = "https://customer-assets.emergentagent.com/job_c5baa259-20bb-43f4-9718-d5116589bbe8/artifacts/o90d4mff_dia%20de%20entrenamiento%20con%20jorge%20el%20curioso.mp4";
const INSTAGRAM_DEFAULT = "https://www.instagram.com/curious123tekkers/";

const IMAGES = {
  hero: "https://customer-assets.emergentagent.com/job_4e563cac-59c1-451d-b6a2-231b424045b8/artifacts/02cy67vf_image.png",
  training: "https://customer-assets.emergentagent.com/job_4e563cac-59c1-451d-b6a2-231b424045b8/artifacts/gkpyigf5_image.png",
  huddle: "https://customer-assets.emergentagent.com/job_4e563cac-59c1-451d-b6a2-231b424045b8/artifacts/f57s2cat_image.png",
};

const useCountUp = (end, duration = 2000, start = 0) => {
  const [count, setCount] = useState(start);
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const currentRef = ref.current;
    const observer = new IntersectionObserver(([entry]) => { if (entry.isIntersecting && !isVisible) setIsVisible(true); }, { threshold: 0.5 });
    if (currentRef) observer.observe(currentRef);
    return () => { if (currentRef) observer.unobserve(currentRef); };
  }, [isVisible]);
  useEffect(() => {
    if (!isVisible) return;
    let startTime;
    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const progress = Math.min((currentTime - startTime) / duration, 1);
      setCount(Math.floor((1 - Math.pow(1 - progress, 4)) * (end - start) + start));
      if (progress < 1) requestAnimationFrame(animate);
    };
    requestAnimationFrame(animate);
  }, [isVisible, end, duration, start]);
  return { count, ref };
};

const useScrollReveal = (threshold = 0.1) => {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const currentRef = ref.current;
    const observer = new IntersectionObserver(([entry]) => { if (entry.isIntersecting) setIsVisible(true); }, { threshold, rootMargin: '50px' });
    if (currentRef) observer.observe(currentRef);
    return () => { if (currentRef) observer.unobserve(currentRef); };
  }, [threshold]);
  return { ref, isVisible };
};

const AnimatedCounter = ({ end, suffix = "", prefix = "", duration = 2000, className = "" }) => {
  const { count, ref } = useCountUp(end, duration);
  return <span ref={ref} className={`counter-number ${className}`}>{prefix}{count.toLocaleString()}{suffix}</span>;
};

const AnimatedSection = ({ children, className = "", animation = "fly-up", delay = 0 }) => {
  const { ref, isVisible } = useScrollReveal(0.1);
  const baseTransform = { 'fly-left': 'translateX(-80px)', 'fly-right': 'translateX(80px)', 'fly-up': 'translateY(60px)', 'fly-down': 'translateY(-60px)', 'zoom': 'scale(0.8)', 'bounce': 'scale(0.5)' };
  const style = { opacity: isVisible ? 1 : 0, transform: isVisible ? 'translateX(0) translateY(0) scale(1)' : baseTransform[animation] || 'translateY(60px)', transition: `opacity 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94) ${delay}ms, transform 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94) ${delay}ms` };
  return <div ref={ref} className={className} style={style}>{children}</div>;
};

const InteractiveCard = ({ children, className = "" }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const cardRef = useRef(null);
  const handleMouseMove = (e) => { if (!cardRef.current) return; const rect = cardRef.current.getBoundingClientRect(); setMousePos({ x: (e.clientX - rect.left - rect.width / 2) / 20, y: (e.clientY - rect.top - rect.height / 2) / 20 }); };
  return (
    <div ref={cardRef} className={`interactive-card hover-glow ripple ${className}`} onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => { setIsHovered(false); setMousePos({ x: 0, y: 0 }); }} onMouseMove={handleMouseMove}
      style={{ transform: isHovered ? `perspective(1000px) rotateY(${mousePos.x}deg) rotateX(${-mousePos.y}deg) translateY(-10px)` : 'perspective(1000px) rotateY(0) rotateX(0)' }}>
      {children}
    </div>
  );
};

const VideoHeroSection = () => {
  const videoRef = useRef(null);
  const { t } = useLanguage();
  const { get } = usePageContent("home");
  const videoUrl = get("hero_video", VIDEO_DEFAULT);
  useEffect(() => { if (videoRef.current) videoRef.current.play().catch(() => {}); }, []);
  return (
    <section className="relative w-full h-screen pt-20 overflow-hidden bg-black" data-testid="video-hero-section">
      <video ref={videoRef} className="absolute inset-0 w-full h-full object-cover" src={videoUrl} autoPlay muted loop playsInline data-testid="hero-video" />
      <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-transparent to-black/60" />
      <div className="relative z-10 flex flex-col items-center justify-end h-full pb-16 px-4 text-center">
        <h1 className="text-4xl sm:text-5xl lg:text-7xl font-bold text-white leading-tight mb-4 font-['Kanit'] drop-shadow-lg">Curious <span className="text-[#87CEEB]">123</span></h1>
        <p className="text-xl sm:text-2xl text-white/90 font-medium mb-8 drop-shadow-md font-['Kanit']">{t.videoHero.tagline}</p>
        <Link to="/private-coaching" data-testid="video-learn-more-btn">
          <Button className="rounded-full px-10 py-4 text-lg font-bold uppercase tracking-wider bg-white text-black hover:bg-[#87CEEB] hover:text-black transition-all">
            {t.videoHero.learnMore}
            <ChevronRight className="w-5 h-5 ml-2" />
          </Button>
        </Link>
      </div>
    </section>
  );
};

const HeroSection = () => {
  const { ref: statsRef, isVisible: statsVisible } = useScrollReveal();
  const { t } = useLanguage();
  const { get } = usePageContent("home");
  const heroImage = get("hero_image", IMAGES.hero);
  const instagramUrl = get("instagram_url", INSTAGRAM_DEFAULT);
  const gofundmeUrl = get("gofundme_url", GOFUNDME_DEFAULT);
  return (
    <section className="relative min-h-screen flex items-center hero-gradient pt-20 overflow-hidden">
      <div className="absolute top-20 left-10 w-20 h-20 bg-[#87CEEB]/20 rounded-full animate-float blur-xl"></div>
      <div className="absolute top-40 right-20 w-32 h-32 bg-[#87CEEB]/10 rounded-full animate-float-slow blur-xl"></div>
      <div className="absolute bottom-40 left-1/4 w-16 h-16 bg-[#87CEEB]/15 rounded-full animate-float blur-lg"></div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          <AnimatedSection animation="fly-left" className="hero-content">
            <span className="inline-block bg-[#87CEEB]/20 text-black px-4 py-2 rounded-full text-sm font-medium mb-6 hover-lift cursor-default">
              <Sparkles className="w-4 h-4 inline mr-2" />{t.hero.location}
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-black leading-tight mb-4 font-['Kanit']">
              {t.hero.heading1}{" "}<span className="relative inline-block">{t.hero.heading2}<span className="absolute -bottom-2 left-0 w-full h-3 bg-[#87CEEB]/40 -z-10 animate-pulse"></span></span>
            </h1>
            <p className="text-xl font-semibold text-[#87CEEB] mb-4 font-['Kanit']">{t.hero.tagline}</p>
            <p className="text-base text-gray-600 mb-6 max-w-lg">{t.hero.description}</p>
          </AnimatedSection>
          <AnimatedSection animation="fly-right" delay={200} className="relative">
            <div className="relative rounded-2xl overflow-hidden shadow-2xl hover-tilt">
              <img src={heroImage} alt="Coach Jorge with youth soccer players" className="w-full h-[400px] md:h-[500px] object-cover transition-transform duration-500 hover:scale-105" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent"></div>
            </div>
            <div ref={statsRef} className={`absolute -bottom-6 -left-6 bg-white p-4 rounded-2xl shadow-lg animate-float hover-glow cursor-pointer ${statsVisible ? 'animate-bounce-in' : 'opacity-0'}`}>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-[#87CEEB] rounded-full flex items-center justify-center animate-wiggle"><Users className="w-6 h-6 text-black" /></div>
                <div><p className="font-bold text-black font-['Kanit'] text-2xl"><AnimatedCounter end={37} duration={1500} /></p><p className="text-sm text-gray-600">{t.mission.equipment.label}</p></div>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </div>
      <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-white to-transparent"></div>
    </section>
  );
};

const PillarsSection = () => {
  const { t } = useLanguage();
  const pillars = [
    { number: "1", title: t.pillars.train.title, description: t.pillars.train.desc, color: "from-blue-400 to-cyan-400" },
    { number: "2", title: t.pillars.play.title, description: t.pillars.play.desc, color: "from-pink-400 to-red-400" },
    { number: "3", title: t.pillars.succeed.title, description: t.pillars.succeed.desc, color: "from-yellow-400 to-orange-400" }
  ];
  return (
    <section className="py-20 md:py-32 bg-white overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <AnimatedSection animation="fly-down" className="text-center mb-16">
          <h2 className="text-3xl sm:text-4xl font-bold text-black mb-4 font-['Kanit']">{t.pillars.sectionTitle} <span className="text-[#87CEEB]">{t.pillars.sectionTitleAccent}</span></h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">{t.pillars.sectionDesc}</p>
        </AnimatedSection>
        <div className="grid md:grid-cols-3 gap-8">
          {pillars.map((pillar, index) => (
            <div key={pillar.number} className="animate-fade-in" style={{ animationDelay: `${index * 150}ms` }}>
              <Card className="pillar-card bg-white border-gray-100 shadow-lg rounded-2xl overflow-hidden h-full hover:shadow-xl transition-all duration-300 hover:-translate-y-2">
                <CardHeader className="pb-4">
                  <div className="flex flex-col items-center gap-2 mb-2">
                    <div className={`w-14 h-14 bg-gradient-to-br ${pillar.color} rounded-full flex items-center justify-center shadow-lg animate-float`} style={{ animationDelay: `${index * 0.3}s` }}>
                      <span className="text-2xl font-bold text-white font-['Kanit']">{pillar.number}</span>
                    </div>
                    <CardTitle className="text-2xl font-bold text-black font-['Kanit']">{pillar.title}</CardTitle>
                  </div>
                </CardHeader>
                <CardContent><CardDescription className="text-gray-600 text-base leading-relaxed text-center">{pillar.description}</CardDescription></CardContent>
              </Card>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const CoreFocusSection = () => {
  const { t } = useLanguage();
  const pillarsOfControl = [
    { icon: Zap, title: t.coreFocus.agility.title, description: t.coreFocus.agility.desc, color: "from-yellow-400 to-orange-500" },
    { icon: CircleDot, title: t.coreFocus.ballControl.title, description: t.coreFocus.ballControl.desc, color: "from-blue-400 to-cyan-500" },
    { icon: Eye, title: t.coreFocus.spacing.title, description: t.coreFocus.spacing.desc, color: "from-green-400 to-emerald-500" },
    { icon: MousePointer, title: t.coreFocus.firstTouch.title, description: t.coreFocus.firstTouch.desc, color: "from-purple-400 to-pink-500" }
  ];
  return (
    <section className="py-20 md:py-32 bg-[#F0F9FF] overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <AnimatedSection animation="fly-down" className="text-center mb-16">
          <span className="inline-block bg-[#87CEEB] text-black px-4 py-2 rounded-full text-sm font-semibold mb-4 uppercase tracking-wider">{t.coreFocus.badge}</span>
          <h2 className="text-3xl sm:text-4xl font-bold text-black mb-4 font-['Kanit']">{t.coreFocus.title}</h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">{t.coreFocus.desc}</p>
        </AnimatedSection>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {pillarsOfControl.map((pillar, index) => (
            <div key={pillar.title} className="animate-fade-in" style={{ animationDelay: `${index * 150}ms` }}>
              <Card className="bg-white border-gray-100 shadow-sm rounded-2xl overflow-hidden h-full group hover:shadow-xl transition-all duration-300 hover:-translate-y-2">
                <CardContent className="p-6 text-center">
                  <div className={`w-16 h-16 bg-gradient-to-br ${pillar.color} rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg animate-float group-hover:scale-110 transition-transform duration-300`} style={{ animationDelay: `${index * 0.2}s` }}>
                    <pillar.icon className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="font-bold text-lg text-black mb-2 font-['Kanit']">{pillar.title}</h3>
                  <p className="text-gray-600 text-sm">{pillar.description}</p>
                </CardContent>
              </Card>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const InstagramSection = () => {
  const { t } = useLanguage();
  const { get } = usePageContent("home");
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const instagramUrl = get("instagram_url", INSTAGRAM_DEFAULT);

  return (
    <section className="py-20 md:py-32 overflow-hidden relative" data-testid="instagram-section"
      style={{ background: "linear-gradient(135deg, #833ab4 0%, #fd1d1d 50%, #fcb045 100%)" }}>
      <div className="absolute inset-0 bg-black/20"></div>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <AnimatedSection animation="fly-down" className="text-center mb-10">
          <span className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm text-white px-5 py-2 rounded-full text-sm font-semibold mb-6 uppercase tracking-wider">
            <Instagram className="w-4 h-4" />
            {t.instagram.badge}
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4 font-['Kanit'] drop-shadow-lg">
            {t.instagram.title}
          </h2>
          <p className="text-lg text-white/90 max-w-2xl mx-auto mb-6 drop-shadow-md">{t.instagram.desc}</p>
          <a href={instagramUrl} target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-white text-black px-8 py-3 rounded-full font-bold uppercase tracking-wider hover:scale-105 transition-transform duration-300 shadow-lg"
            data-testid="instagram-follow-btn">
            <Instagram className="w-5 h-5" />
            {t.instagram.follow}
          </a>
        </AnimatedSection>

        <div className="max-w-lg mx-auto">
          <div className="bg-white rounded-2xl overflow-hidden shadow-2xl" style={{ minHeight: '480px' }}>
            {!iframeLoaded && (
              <div className="flex flex-col items-center justify-center py-16 gap-4">
                <div className="w-10 h-10 border-4 border-[#87CEEB] border-t-transparent rounded-full animate-spin"></div>
                <p className="text-gray-500 text-sm">Loading latest posts...</p>
              </div>
            )}
            <iframe
              src="https://www.instagram.com/curious123tekkers/embed/"
              className={`w-full border-0 ${iframeLoaded ? '' : 'h-0'}`}
              style={{ minHeight: iframeLoaded ? '480px' : '0' }}
              title="Curious 123 Instagram Feed"
              scrolling="no"
              allowtransparency="true"
              onLoad={() => setIframeLoaded(true)}
              data-testid="instagram-embed-iframe"
            />
          </div>
        </div>

        <div className="text-center mt-8">
          <a href={instagramUrl} target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-white/80 hover:text-white font-semibold transition-colors"
            data-testid="instagram-view-profile">
            {t.instagram.viewProfile}
            <ExternalLink className="w-4 h-4" />
          </a>
        </div>
      </div>
    </section>
  );
};

const FutbolParaTodosSection = () => {
  const { t } = useLanguage();
  return (
    <section className="py-20 md:py-28 bg-white overflow-hidden" data-testid="futbol-para-todos-section">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <AnimatedSection animation="fly-up">
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-black mb-6 font-['Kanit']">
            {t.futbolParaTodos.title}
          </h2>
          <p className="text-lg sm:text-xl text-gray-700 leading-relaxed">
            <span className="font-bold text-[#87CEEB]">{t.futbolParaTodos.mission} </span>
            {t.futbolParaTodos.desc}
          </p>
        </AnimatedSection>
      </div>
    </section>
  );
};

const RealitySection = () => {
  const { t } = useLanguage();
  const { get } = usePageContent("home");
  return (
    <section className="py-20 md:py-32 bg-black text-white overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          <AnimatedSection animation="fly-left">
            <span className="inline-block bg-[#87CEEB] text-black px-4 py-2 rounded-full text-sm font-semibold mb-6 uppercase tracking-wider hover-shake cursor-default">{t.reality.badge}</span>
            <h2 className="text-3xl sm:text-4xl font-bold mb-6 font-['Kanit']">{t.reality.title}</h2>
            <div className="space-y-4 text-gray-300">
              <p className="text-lg">{t.reality.p1} <strong className="text-white">{t.reality.p1Bold}</strong>{t.reality.p1End}</p>
              <p className="text-xl font-medium text-[#87CEEB] animate-pulse">{t.reality.p2}</p>
              <p>{t.reality.p3}</p>
            </div>
            <div className="mt-8">
              <a href={GOFUNDME_DEFAULT} target="_blank" rel="noopener noreferrer">
                <Button className="donate-btn btn-shine rounded-full px-8 py-3 font-semibold uppercase tracking-wider hover-lift animate-pulse-glow">
                  {t.reality.cta}<ExternalLink className="w-4 h-4 ml-2" />
                </Button>
              </a>
            </div>
          </AnimatedSection>
          <AnimatedSection animation="fly-right" delay={200} className="relative">
            <div className="rounded-2xl overflow-hidden shadow-xl hover-tilt">
              <img src={get("huddle_image", IMAGES.huddle)} alt="Team huddle" className="w-full h-[400px] object-cover transition-transform duration-500 hover:scale-105" />
            </div>
            <div className="absolute -bottom-6 -right-6 w-32 h-32 bg-[#87CEEB] rounded-2xl -z-10 opacity-50 animate-float"></div>
          </AnimatedSection>
        </div>
      </div>
    </section>
  );
};

const MissionSection = () => {
  const { t } = useLanguage();
  const { get } = usePageContent("home");
  const missionItems = [
    { icon: Trophy, title: t.mission.equipment.title, description: t.mission.equipment.desc, stat: 37, statLabel: t.mission.equipment.label },
    { icon: Target, title: t.mission.coaching.title, description: t.mission.coaching.desc, stat: 6, statLabel: t.mission.coaching.label },
    { icon: BookOpen, title: t.mission.academic.title, description: t.mission.academic.desc, stat: 100, statLabel: t.mission.academic.label },
    { icon: Car, title: t.mission.transport.title, description: t.mission.transport.desc, stat: 2, statLabel: t.mission.transport.label }
  ];
  return (
    <section className="py-20 md:py-32 bg-[#F0F9FF] overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <AnimatedSection animation="fly-down" className="text-center mb-16">
          <span className="inline-block bg-[#87CEEB] text-black px-4 py-2 rounded-full text-sm font-semibold mb-4 uppercase tracking-wider">{t.mission.badge}</span>
          <h2 className="text-3xl sm:text-4xl font-bold text-black mb-4 font-['Kanit']">{t.mission.title}</h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">{t.mission.desc}</p>
        </AnimatedSection>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {missionItems.map((item, index) => (
            <AnimatedSection key={item.title} animation="zoom" delay={index * 100}>
              <InteractiveCard className="h-full">
                <Card className="bg-white border-gray-100 shadow-sm rounded-2xl overflow-hidden h-full">
                  <CardContent className="p-6 text-center">
                    <div className="w-16 h-16 bg-[#87CEEB] rounded-full flex items-center justify-center mx-auto mb-4 animate-float" style={{ animationDelay: `${index * 0.2}s` }}>
                      <item.icon className="w-8 h-8 text-black" />
                    </div>
                    <div className="mb-2">
                      <span className="text-3xl font-bold text-[#87CEEB] font-['Kanit']"><AnimatedCounter end={item.stat} duration={1500 + index * 200} /></span>
                      <span className="text-sm text-gray-500 ml-1">{item.statLabel}</span>
                    </div>
                    <h3 className="font-bold text-lg text-black mb-2 font-['Kanit']">{item.title}</h3>
                    <p className="text-gray-600 text-sm">{item.description}</p>
                  </CardContent>
                </Card>
              </InteractiveCard>
            </AnimatedSection>
          ))}
        </div>
      </div>
    </section>
  );
};

const PricingSection = () => {
  const { lang } = useLanguage();
  return (
    <section className="py-20 bg-[#F0F9FF]" data-testid="home-pricing-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <AnimatedSection animation="fly-down" className="text-center mb-14">
          <span className="inline-block bg-[#87CEEB] text-black px-4 py-2 rounded-full text-sm font-semibold mb-4 uppercase tracking-wider">{lang === "es" ? "Programas" : "Training Programs"}</span>
          <h2 className="text-3xl sm:text-4xl font-bold text-black mb-4 font-['Kanit']">{lang === "es" ? "Detalles del Programa y Precios" : "Program Details & Pricing"}</h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">{lang === "es" ? "Elige el formato de entrenamiento que mejor se adapte a tu joven atleta." : "Choose the training format that best fits your young athlete."}</p>
        </AnimatedSection>

        {/* Full Team Training (side by side: Tue/Thu Ages 8-10 & Mon/Wed Ages 6-8) */}
        <AnimatedSection animation="fly-up">
          <div className="mb-5">
            <h3 className="text-xl font-bold text-gray-800 font-['Kanit'] mb-1">{lang === "es" ? "Equipo Completo" : "Full Team Training"}</h3>
            <p className="text-sm text-gray-500">{lang === "es" ? "4:00 PM - 5:00 PM | Sesion mensual (4 semanas)" : "4:00 PM - 5:00 PM | Monthly session (4 weeks)"}</p>
          </div>
        </AnimatedSection>
        <div className="grid md:grid-cols-2 gap-5 mb-10" data-testid="home-full-team-section">
          <AnimatedSection animation="fly-left">
            <div className="bg-white rounded-2xl border-2 border-[#87CEEB] overflow-hidden h-full shadow-sm hover:shadow-xl transition-all duration-300">
              <div className="bg-[#87CEEB] text-black px-5 py-3 flex items-center justify-between">
                <div className="flex items-center gap-2"><Users className="w-5 h-5" /><h3 className="font-bold font-['Kanit']">{lang === "es" ? "Lunes y Miercoles" : "Mondays & Wednesdays"}</h3></div>
                <span className="text-xl font-bold font-['Kanit']">$50</span>
              </div>
              <div className="p-5 space-y-2.5">
                <p className="text-xs text-gray-400 font-medium uppercase tracking-wider">{lang === "es" ? "Edades 6 - 8 anos | por 8 sesiones" : "Ages 6 - 8 years old | for 8 sessions"}</p>
                <div className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-[#87CEEB] flex-shrink-0 mt-0.5" /><p className="text-gray-700 text-sm">{lang === "es" ? "Min 8 / Max 15 jugadores" : "Min 8 / Max 15 players"}</p></div>
                <div className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-[#87CEEB] flex-shrink-0 mt-0.5" /><p className="text-gray-700 text-sm">{lang === "es" ? "Pies rapidos, control, primer toque, duelos 1v1/2v2" : "Fast-feet, ball control, first touch, 1v1/2v2 duels"}</p></div>
                <div className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-[#87CEEB] flex-shrink-0 mt-0.5" /><p className="text-gray-700 text-sm">{lang === "es" ? "Espacios limitados" : "Limited spots for personalized attention"}</p></div>
              </div>
            </div>
          </AnimatedSection>
          <AnimatedSection animation="fly-right" delay={100}>
            <div className="bg-white rounded-2xl border-2 border-[#87CEEB] overflow-hidden h-full shadow-sm hover:shadow-xl transition-all duration-300">
              <div className="bg-[#87CEEB] text-black px-5 py-3 flex items-center justify-between">
                <div className="flex items-center gap-2"><Users className="w-5 h-5" /><h3 className="font-bold font-['Kanit']">{lang === "es" ? "Martes y Jueves" : "Tuesdays & Thursdays"}</h3></div>
                <span className="text-xl font-bold font-['Kanit']">$50</span>
              </div>
              <div className="p-5 space-y-2.5">
                <p className="text-xs text-gray-400 font-medium uppercase tracking-wider">{lang === "es" ? "Edades 8 - 10 anos | por 8 sesiones" : "Ages 8 - 10 years old | for 8 sessions"}</p>
                <div className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-[#87CEEB] flex-shrink-0 mt-0.5" /><p className="text-gray-700 text-sm">{lang === "es" ? "Min 8 / Max 15 jugadores" : "Min 8 / Max 15 players"}</p></div>
                <div className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-[#87CEEB] flex-shrink-0 mt-0.5" /><p className="text-gray-700 text-sm">{lang === "es" ? "Pies rapidos, control, primer toque, duelos 1v1/2v2" : "Fast-feet, ball control, first touch, 1v1/2v2 duels"}</p></div>
                <div className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-[#87CEEB] flex-shrink-0 mt-0.5" /><p className="text-gray-700 text-sm">{lang === "es" ? "Espacios limitados" : "Limited spots for personalized attention"}</p></div>
              </div>
            </div>
          </AnimatedSection>
        </div>

        {/* Section 3: Small Group + Private Training (side by side) */}
        <AnimatedSection animation="fly-up" delay={200}>
          <div className="mb-5">
            <h3 className="text-xl font-bold text-gray-800 font-['Kanit'] mb-1">{lang === "es" ? "Entrenamiento Adicional" : "Additional Training Options"}</h3>
            <p className="text-sm text-gray-500">{lang === "es" ? "Sesiones de grupo pequeno y privadas disponibles" : "Small group and private sessions available"}</p>
          </div>
        </AnimatedSection>
        <div className="grid md:grid-cols-2 gap-5 mb-10" data-testid="home-group-private-section">
          <AnimatedSection animation="fly-left" delay={300}>
            <div className="bg-white rounded-2xl border-2 border-emerald-400 overflow-hidden h-full shadow-sm hover:shadow-xl transition-all duration-300">
              <div className="bg-emerald-400 text-black px-5 py-3 flex items-center justify-between">
                <div className="flex items-center gap-2"><Zap className="w-5 h-5" /><h3 className="font-bold font-['Kanit']">{lang === "es" ? "Grupo Pequeno" : "Small Group Training"}</h3></div>
                <span className="text-xl font-bold font-['Kanit']">$20</span>
              </div>
              <div className="p-5 space-y-2.5">
                <p className="text-xs text-gray-400 font-medium uppercase tracking-wider">{lang === "es" ? "por jugador" : "/ player"}</p>
                <div className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" /><p className="text-gray-700 text-sm">{lang === "es" ? "2-4 jugadores por grupo" : "2-4 players per group"}</p></div>
                <div className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" /><p className="text-gray-700 text-sm">{lang === "es" ? "Quimica y dinamica tecnica" : "Chemistry and technical dynamics"}</p></div>
                <div className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" /><p className="text-gray-700 text-sm">{lang === "es" ? "Espacios limitados" : "Limited spots for personalized attention"}</p></div>
              </div>
            </div>
          </AnimatedSection>
          <AnimatedSection animation="fly-right" delay={400}>
            <div className="bg-white rounded-2xl border-2 border-amber-400 overflow-hidden h-full shadow-sm hover:shadow-xl transition-all duration-300">
              <div className="bg-amber-400 text-black px-5 py-3 flex items-center justify-between">
                <div className="flex items-center gap-2"><Target className="w-5 h-5" /><h3 className="font-bold font-['Kanit']">{lang === "es" ? "Entrenamiento Privado" : "Private Training"}</h3></div>
                <span className="text-xl font-bold font-['Kanit']">$35</span>
              </div>
              <div className="p-5 space-y-2.5">
                <p className="text-xs text-gray-400 font-medium uppercase tracking-wider">{lang === "es" ? "por sesion" : "/ session"}</p>
                <div className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" /><p className="text-gray-700 text-sm">{lang === "es" ? "Correccion de habilidades 1-a-1" : "1-on-1 personalized skill correction"}</p></div>
                <div className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" /><p className="text-gray-700 text-sm">{lang === "es" ? "Desarrollo de elite" : "Elite development tailored to your athlete"}</p></div>
                <div className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" /><p className="text-gray-700 text-sm">{lang === "es" ? "Horario flexible" : "Flexible scheduling"}</p></div>
              </div>
            </div>
          </AnimatedSection>
        </div>

        {/* Video Analytics strip + Coaching link */}
        <AnimatedSection animation="fly-up" delay={300}>
          <div className="bg-violet-50 border border-violet-200 rounded-xl px-6 py-4 flex flex-col sm:flex-row items-center justify-between gap-3 mb-8">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-violet-400 rounded-lg flex items-center justify-center"><Crosshair className="w-5 h-5 text-white" /></div>
              <div>
                <span className="font-bold text-gray-800 font-['Kanit']">{lang === "es" ? "Video Analitico" : "Video Analytics Add-on"}</span>
                <p className="text-sm text-gray-500">{lang === "es" ? "Revisiones profesionales usando video." : "Professional performance reviews using video."}</p>
              </div>
            </div>
            <span className="text-xl font-bold text-violet-600 font-['Kanit'] whitespace-nowrap">+$15 <span className="text-sm font-normal text-gray-400">{lang === "es" ? "/ sesion" : "/ session"}</span></span>
          </div>
          <div className="text-center">
            <Link to="/private-coaching" data-testid="home-coaching-link">
              <Button className="bg-black text-white hover:bg-gray-900 rounded-full px-10 py-4 text-lg font-bold uppercase tracking-wider">
                {lang === "es" ? "Ver Todos los Detalles" : "View Full Coaching Details"} <ChevronRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>
        </AnimatedSection>
      </div>
    </section>
  );
};

const HelpSection = () => {
  const [donateHover, setDonateHover] = useState(false);
  const [shareHover, setShareHover] = useState(false);
  const { t } = useLanguage();
  return (
    <section className="py-20 md:py-32 bg-white overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <AnimatedSection animation="fly-down" className="text-center mb-16">
          <span className="inline-block bg-[#87CEEB]/20 text-black px-4 py-2 rounded-full text-sm font-medium mb-4">{t.help.badge}</span>
          <h2 className="text-3xl sm:text-4xl font-bold text-black mb-4 font-['Kanit']">{t.help.title}</h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            <strong className="text-[#87CEEB]"><AnimatedCounter end={37} /></strong> {t.help.desc1} <strong className="text-[#87CEEB]"><AnimatedCounter end={6} /></strong> {t.help.desc2}
          </p>
        </AnimatedSection>
        <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
          <AnimatedSection animation="fly-left">
            <div className={`relative overflow-hidden bg-[#87CEEB]/10 border-[#87CEEB] border-2 rounded-2xl transition-all duration-500 ${donateHover ? 'scale-105 shadow-2xl' : ''}`} onMouseEnter={() => setDonateHover(true)} onMouseLeave={() => setDonateHover(false)}>
              <div className={`absolute inset-0 bg-gradient-to-br from-[#87CEEB]/20 to-transparent transition-opacity duration-500 ${donateHover ? 'opacity-100' : 'opacity-0'}`}></div>
              <CardContent className="p-8 text-center relative z-10">
                <div className={`w-20 h-20 bg-[#87CEEB] rounded-full flex items-center justify-center mx-auto mb-6 transition-all duration-500 ${donateHover ? 'animate-bounce scale-110' : 'animate-float'}`}>
                  <Heart className={`w-10 h-10 text-black transition-transform duration-300 ${donateHover ? 'scale-125' : ''}`} />
                </div>
                <h3 className="font-bold text-2xl text-black mb-4 font-['Kanit']">{t.help.donateTitle}</h3>
                <p className="text-gray-600 mb-4"><strong>$<AnimatedCounter end={7} /></strong> {t.help.donateDesc}</p>
                <div className="mb-6 p-4 bg-white/50 rounded-xl">
                  <p className="text-sm text-gray-500">{t.help.impact}</p>
                  <div className="flex justify-around mt-2">
                    <div className="text-center"><p className="font-bold text-[#87CEEB] font-['Kanit']">$<AnimatedCounter end={15} /></p><p className="text-xs text-gray-500">{t.help.shinGuards}</p></div>
                    <div className="text-center"><p className="font-bold text-[#87CEEB] font-['Kanit']">$<AnimatedCounter end={35} /></p><p className="text-xs text-gray-500">{t.help.cleats}</p></div>
                    <div className="text-center"><p className="font-bold text-[#87CEEB] font-['Kanit']">$<AnimatedCounter end={50} /></p><p className="text-xs text-gray-500">{t.help.fullKit}</p></div>
                  </div>
                </div>
                <a href={GOFUNDME_DEFAULT} target="_blank" rel="noopener noreferrer">
                  <Button className="donate-btn btn-shine rounded-full px-8 py-3 font-semibold uppercase tracking-wider w-full animate-pulse-glow">{t.help.donateBtn}</Button>
                </a>
              </CardContent>
            </div>
          </AnimatedSection>
          <AnimatedSection animation="fly-right" delay={200}>
            <div className={`relative overflow-hidden bg-black text-white rounded-2xl transition-all duration-500 ${shareHover ? 'scale-105 shadow-2xl' : ''}`} onMouseEnter={() => setShareHover(true)} onMouseLeave={() => setShareHover(false)}>
              <div className={`absolute inset-0 bg-gradient-to-br from-[#87CEEB]/30 to-transparent transition-opacity duration-500 ${shareHover ? 'opacity-100' : 'opacity-0'}`}></div>
              <CardContent className="p-8 text-center relative z-10">
                <div className={`w-20 h-20 bg-[#87CEEB] rounded-full flex items-center justify-center mx-auto mb-6 transition-all duration-500 ${shareHover ? 'animate-wiggle scale-110' : 'animate-float'}`}>
                  <Share2 className={`w-10 h-10 text-black transition-transform duration-300 ${shareHover ? 'scale-125' : ''}`} />
                </div>
                <h3 className="font-bold text-2xl mb-4 font-['Kanit']">{t.help.shareTitle}</h3>
                <p className="text-gray-300 mb-6">{t.help.shareDesc}</p>
                <div className="mb-6 p-4 bg-white/10 rounded-xl">
                  <p className="text-sm text-gray-400 italic">{t.help.shareQuote}</p>
                  <p className="text-xs text-gray-500 mt-2">{t.help.shareHook}</p>
                </div>
                <Button variant="outline" className="rounded-full px-8 py-3 font-semibold border-2 border-[#87CEEB] text-[#87CEEB] hover:bg-[#87CEEB] hover:text-black w-full transition-all duration-300"
                  onClick={() => { if (navigator.share) { navigator.share({ title: t.help.shareNative.title, text: t.help.shareNative.text, url: GOFUNDME_DEFAULT }); } else { navigator.clipboard.writeText(GOFUNDME_DEFAULT); toast.success(t.help.linkCopied); } }}>
                  {t.help.shareBtn}
                </Button>
              </CardContent>
            </div>
          </AnimatedSection>
        </div>
      </div>
    </section>
  );
};

const CTASection = () => {
  const { t } = useLanguage();
  return (
    <section className="py-20 bg-[#87CEEB]">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <AnimatedSection animation="zoom">
          <h2 className="text-3xl sm:text-4xl font-bold text-black mb-6 font-['Kanit']">{t.cta.title}</h2>
          <p className="text-lg text-black/80 mb-8">{t.cta.desc}</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href={GOFUNDME_DEFAULT} target="_blank" rel="noopener noreferrer">
              <Button className="bg-black text-white hover:bg-gray-900 rounded-full px-10 py-4 text-lg font-bold uppercase tracking-wider hover-lift"><Heart className="w-5 h-5 mr-2" />{t.cta.donate}</Button>
            </a>
            <Link to="/events">
              <Button variant="outline" className="rounded-full px-8 py-4 text-lg border-2 border-black text-black hover:bg-black hover:text-white transition-all hover-lift">{t.cta.events}</Button>
            </Link>
          </div>
        </AnimatedSection>
      </div>
    </section>
  );
};

const HomePage = () => {
  const { lang } = useLanguage();
  const title = lang === "es" ? "Curious 123 | Programa de Futbol Juvenil en San Juan del Sur, Nicaragua" : "Curious 123 | Youth Soccer Program in San Juan del Sur, Nicaragua";
  const desc = lang === "es" ? "Curious 123 cambia la vida de 37 ninos en San Juan del Sur, Nicaragua a traves del futbol. Dona hoy!" : "Curious 123 is changing lives of 37 kids in San Juan del Sur, Nicaragua through soccer. Donate today!";

  return (
    <>
      <Helmet>
        <html lang={lang} />
        <title>{title}</title>
        <meta name="description" content={desc} />
        <meta name="keywords" content="youth soccer, Nicaragua, San Juan del Sur, charity, donation, kids soccer, Curious 123, Coach Jorge, futbol juvenil" />
        <link rel="canonical" href="https://curious123.org/" />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={desc} />
        <meta property="og:image" content="https://customer-assets.emergentagent.com/job_a9038138-35cf-4d7a-8b3b-ccb6b5c034cc/artifacts/7e22fcgr_image.png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:url" content="https://curious123.org/" />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Curious 123" />
        <meta property="og:locale" content={lang === "es" ? "es_NI" : "en_US"} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={desc} />
        <meta name="twitter:image" content="https://customer-assets.emergentagent.com/job_a9038138-35cf-4d7a-8b3b-ccb6b5c034cc/artifacts/7e22fcgr_image.png" />
        <script type="application/ld+json">{`
          {"@context":"https://schema.org","@type":"NonprofitOrganization","name":"Curious 123","description":"${desc}","url":"https://curious123.org","logo":"https://customer-assets.emergentagent.com/job_a9038138-35cf-4d7a-8b3b-ccb6b5c034cc/artifacts/7e22fcgr_image.png","foundingDate":"2024","founder":{"@type":"Person","name":"Jorge Luis Carcamo"},"address":{"@type":"PostalAddress","addressLocality":"San Juan del Sur","addressCountry":"NI"},"sameAs":["https://gofund.me/274183352"]}
        `}</script>
      </Helmet>
      <VideoHeroSection />
      <HeroSection />
      <PillarsSection />
      <CoreFocusSection />
      <PricingSection />
      <InstagramSection />
      <FutbolParaTodosSection />
      <RealitySection />
      <MissionSection />
      <HelpSection />
      <CTASection />
    </>
  );
};

export default HomePage;
